import { useMemo, useRef } from 'react';
import * as React from 'react';

import { AccordionContext } from './AccordionContext';
import { ACCORDION_BUTTON_ID_PREFIX } from './constants';

export interface AccordionProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * An accordion is a vertically stacked set of interactive headings that each
 * contain a title representing a section of content. The headings function as
 * controls that enable users to reveal or hide their associated sections of
 * content.
 *
 * Accordions are commonly used to reduce the need to scroll when presenting
 * multiple sections of content on a single page.
 */
export function Accordion({ children, className }: AccordionProps) {
  const elementRef = useRef<HTMLDivElement>(null);

  const context = useMemo(
    () => ({
      focusFirstButton() {
        const buttons = queryAllButtons(elementRef.current);
        const firstButton = buttons[0];
        if (firstButton instanceof HTMLElement) {
          firstButton.focus();
        }
      },
      focusLastButton() {
        const buttons = queryAllButtons(elementRef.current);
        const lastButton = buttons[buttons.length - 1];
        if (lastButton instanceof HTMLElement) {
          lastButton.focus();
        }
      },
      focusNextButton(currentButton: HTMLElement) {
        const buttons = queryAllButtons(elementRef.current);
        const currentIndex = buttons.indexOf(currentButton);
        if (currentIndex !== -1) {
          const nextButton = buttons[currentIndex + 1];
          if (nextButton instanceof HTMLElement) {
            nextButton.focus();
          }
        }
      },
      focusPreviousButton(currentButton: HTMLElement) {
        const buttons = queryAllButtons(elementRef.current);
        const currentIndex = buttons.indexOf(currentButton);
        if (currentIndex !== -1) {
          const previousButton = buttons[currentIndex - 1];
          if (previousButton instanceof HTMLElement) {
            previousButton.focus();
          }
        }
      },
    }),
    [],
  );

  return (
    <AccordionContext.Provider value={context}>
      <div className={className} ref={elementRef}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

function queryAllButtons(accordionElement: HTMLElement | null) {
  if (!accordionElement) {
    return [];
  }

  return Array.from(accordionElement.querySelectorAll(`[id^="${ACCORDION_BUTTON_ID_PREFIX}"]`));
}
